import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Search, CheckCircle2, UserPlus, MapPin, Instagram, Users, Star, ChevronDown } from 'lucide-react';
import Button from '../common/design-system/Button';
import { getCreators, inviteRegisteredCreator, getBusinessOffers, getCategories } from '../../services/api';
import styles from './CreatorDiscovery.module.scss';

const CreatorDiscovery = () => {
    const { t } = useTranslation(['business', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const initialOfferId = queryParams.get('offerId');

    const [creators, setCreators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [myOffers, setMyOffers] = useState([]);
    const [selectedOfferId, setSelectedOfferId] = useState(initialOfferId || '');
    const [inviting, setInviting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    // Filters
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedTier, setSelectedTier] = useState('');

    const creatorTiers = [
        { id: 'FOTH', name: 'Friend Of The House' },
        { id: 'NANO', name: 'Nano (3k-10k)' },
        { id: 'MICRO', name: 'Micro (10k-50k)' },
        { id: 'MID', name: 'Mid (50k-200k)' },
        { id: 'MACRO', name: 'Macro (200k-500k)' },
        { id: 'CELEB', name: 'Celebrity (500k+)' },
    ];

    const fetchCreators = useCallback(async (page = 1) => {
        if (page === 1) setLoading(true);
        else setLoadingMore(true);
        
        try {
            const params = { 
                search: searchTerm, 
                page,
                category: selectedCategory,
                tier: selectedTier
            };
            const res = await getCreators(params);
            const newData = res.data.results || res.data;
            
            if (page === 1) {
                setCreators(newData);
            } else {
                setCreators(prev => [...prev, ...newData]);
            }
            
            setHasMore(Boolean(res.data.next));
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching creators:', error);
            toast.error(t('common:error_load', 'Error al cargar creadores'));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [searchTerm, selectedCategory, selectedTier, t]);

    const fetchMyOffers = useCallback(async () => {
        try {
            const res = await getBusinessOffers();
            const offers = res.data.results || res.data;
            setMyOffers(offers.filter(o => o.status === 'active' || o.status === 'published'));
        } catch (e) {
            console.error('Error fetching business offers:', e);
        }
    }, []);

    const fetchInitialData = useCallback(async () => {
        try {
            const catRes = await getCategories();
            setCategories(catRes.data || []);
        } catch (e) {
            console.error('Error fetching categories:', e);
        }
    }, []);

    useEffect(() => {
        fetchCreators(1);
        fetchMyOffers();
        fetchInitialData();
    }, [fetchCreators, fetchMyOffers, fetchInitialData]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchCreators(1);
    };

    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            fetchCreators(currentPage + 1);
        }
    };

    const openInviteModal = (creator) => {
        setSelectedCreator(creator);
        setShowInviteModal(true);
    };

    const handleInvite = async () => {
        if (!selectedOfferId) {
            toast.warning(t('discovery.select_offer_prompt', 'Selecciona una colaboración'));
            return;
        }

        setInviting(true);
        try {
            await inviteRegisteredCreator(selectedOfferId, selectedCreator.id);
            toast.success(t('discovery.invite_success', { username: selectedCreator.user.username, defaultValue: `Invitación enviada a ${selectedCreator.user.username}` }));
            setShowInviteModal(false);
            navigate(`/business/collabs/${selectedOfferId}/journey?step=creators`);
        } catch (error) {
            toast.error(error.response?.data?.error || t('common:error_default', 'Error al enviar invitación'));
        } finally {
            setInviting(false);
        }
    };


    return (
        <div className={styles['discovery-workspace']}>
            <header className={styles['workspace-hero']}>
                <div className={styles.container}>
                    <div className={styles['hero-layout']}>
                        <div className={styles['title-group']}>
                            <div className={styles['t-icon']}>
                                <Users size={24} />
                            </div>
                            <div>
                                <div className={styles['badge-luxury']}>
                                    <Star size={12} className="mr-1" /> {t('discovery.network_badge', 'Network Exclusiva')}
                                </div>
                                <h1>{t('discovery.title', 'Descubrir Talento')}</h1>
                                <p className={styles.lead}>{t('discovery.subtitle', 'Encuentra a los creadores perfectos para tu marca en nuestra red curada.')}</p>
                            </div>
                        </div>

                        <div className={styles['controls-bar']}>
                            <form className={styles['search-wrapper']} onSubmit={handleSearch}>
                                <Search size={18} />
                                <input 
                                    type="text" 
                                    placeholder={t('discovery.search_placeholder', 'Buscar por nombre o usuario...')} 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button type="submit" className={styles['search-btn']}>{t('discovery.search_btn', 'Buscar')}</button>
                            </form>

                            <div className={styles['filters-group']}>
                                <div className={styles['filter-select']}>
                                    <select 
                                        value={selectedCategory} 
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="">{t('discovery.all_categories', 'Todas las Categorías')}</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.slug}>{cat.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} />
                                </div>

                                <div className={styles['filter-select']}>
                                    <select 
                                        value={selectedTier} 
                                        onChange={(e) => setSelectedTier(e.target.value)}
                                    >
                                        <option value="">{t('discovery.all_tiers', 'Todos los Tiers')}</option>
                                        {creatorTiers.map(tier => (
                                            <option key={tier.id} value={tier.id}>{tier.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className={styles.container}>
                {loading ? (
                    <div className={styles['loading-state']}>
                        <div className={styles.spinner}></div>
                        <p>{t('common:loading', 'Cargando...')}</p>
                    </div>
                ) : creators.length === 0 ? (
                    <div className={styles['empty-state']}>
                        <Users size={48} opacity={0.2} />
                        <p>{t('common:no_results', 'No se encontraron resultados.')}</p>
                    </div>
                ) : (
                    <>
                        <div className={styles['creators-grid']}>
                            {creators.map(creator => (
                                <motion.div 
                                    key={creator.id} 
                                    className={styles['creator-card']}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className={styles['card-header']}>
                                        <div className={styles['avatar-wrap']}>
                                            {creator.ig_profile_picture_url ? (
                                                <img src={creator.ig_profile_picture_url} alt={creator.user.username} />
                                            ) : (
                                                <div className={styles.avatarPlaceholder}>
                                                    {creator.user.username.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles['user-info']}>
                                            <h3>{creator.user.username}</h3>
                                            <p><MapPin size={12} className="mr-1" /> {creator.user.country_name || 'Panamá'}</p>
                                        </div>
                                        {creator.creator_type && creator.creator_type !== 'none' && (
                                            <div className={styles['tier-badge-card']}>
                                                {creator.creator_type}
                                            </div>
                                        )}
                                        {creator.is_verified && <CheckCircle2 className={styles.verified} size={18} />}
                                    </div>

                                    <div className={styles['card-body']}>
                                        <div className={styles.stat}>
                                            <Instagram size={14} />
                                            <span>{creator.ig_followers?.toLocaleString() || '0'} followers</span>
                                        </div>
                                        <div className={styles.stat}>
                                            <Star size={14} />
                                            <span>{creator.ig_media_count?.toLocaleString() || '0'} posts</span>
                                        </div>
                                        
                                        <div className={styles.categories}>
                                            {creator.content_categories?.slice(0, 3).map(cat => (
                                                <span key={cat.id} className={styles.catBadge}>{cat.name}</span>
                                            )) || <span className={styles.catBadge}>General</span>}
                                        </div>
                                    </div>

                                    <div className={styles['card-footer']}>
                                        <Button 
                                            variant="outline" 
                                            fullWidth 
                                            onClick={() => openInviteModal(creator)}
                                        >
                                            <UserPlus size={16} className="mr-2" /> {t('discovery.invite', 'Invitar')}
                                        </Button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {hasMore && (
                            <div className="flex justify-center mt-12 mb-12">
                                <Button 
                                    variant="secondary" 
                                    onClick={handleLoadMore} 
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? t('common:loading', 'Cargando...') : t('discovery.load_more', 'Cargar más creadores')}
                                    {!loadingMore && <ChevronDown size={18} className="ml-2" />}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Invite Modal */}
            <AnimatePresence>
                {showInviteModal && (
                    <div className={styles['modal-overlay']} onClick={() => setShowInviteModal(false)}>
                        <motion.div 
                            className={styles['invite-modal']}
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h2>{t('discovery.modal_title', 'Invitar a colaboración')}</h2>
                            <p>Invita a <strong>@{selectedCreator?.user.username}</strong> a participar en una de tus campañas activas.</p>

                            <div className={styles['form-group']}>
                                <label>{t('discovery.select_offer_prompt', 'Selecciona la Colaboración')}</label>
                                <select 
                                    value={selectedOfferId} 
                                    onChange={(e) => setSelectedOfferId(e.target.value)}
                                    className={styles['form-control']}
                                >
                                    <option value="">{t('discovery.select_offer_prompt', 'Seleccionar una campaña...')}</option>
                                    {myOffers.map(offer => (
                                        <option key={offer.id} value={offer.id}>{offer.title}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles['modal-actions']}>
                                <Button variant="outline" onClick={() => setShowInviteModal(false)}>{t('common:cancel', 'Cancelar')}</Button>
                                <Button 
                                    variant="primary" 
                                    onClick={handleInvite} 
                                    disabled={inviting || !selectedOfferId}
                                >
                                    {inviting ? t('discovery.inviting', 'Enviando...') : t('discovery.invite', 'Enviar Invitación')}
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CreatorDiscovery;
